/**
 * @file customer-management.tsx
 * @module app/modules/sales-management/sales-representative/customer-management/customer-management
 * @description Implements the customer management interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Button, Card, Col, FormLabel, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { KTSVG } from "../../../../../_admin/helpers";
import APICallService from "../../../../../api/apiCallService";
import {
  buyer,
  customerAssignment
} from "../../../../../api/apiEndPoints";
import { customerAssignmentJson } from "../../../../../api/apiJSON/customerAssignment";
import { getKey, setKey } from "../../../../../Global/history";
import Loader from "../../../../../Global/loader";
import Pagination from "../../../../../Global/pagination";
import { success } from "../../../../../Global/toast";
import {
  AllCustomers,
  CustomerAssignment as CustomerAssignmentModule,
  PAGE_LIMIT
} from "../../../../../utils/constants";
import { IMAGES } from "../../../../../utils/assetsAndPermissions";
import Method from "../../../../../utils/methods";
import { CUSTOMER_STATUS, REPRESENTATIVE_STATUS } from "../../../../../utils/staticJSON";
import {
  listCustomerAssignment,
  listCustomers,
} from "../../../../../utils/storeString";
import { CustomSelect } from "../../../../custom/Select/CustomSelect";
import AssignRepModal from "../../../../modals/assign-rep-modal";
import { useAuth } from "../../../auth";
import b1 from "../../../../../_admin/assets/media/product/default.svg";
import green from "../../../../../_admin/assets/media/svg_uTrade/green_dot.svg";
import red from "../../../../../_admin/assets/media/svg_uTrade/red_dot.svg";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";
import ImageWithFallback from "../../../_common/components/image-with-fallback";

const CustomerManagement = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const { currentUser } = useAuth();

  // States for filters and pagination
  const [loading, setLoading] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [search, setSearch] = useState<any>(
    getKey(listCustomerAssignment.search) || ""
  );
  const [selectedStatus, setSelectedStatus] = useState<any>(
    getKey(listCustomerAssignment.status) !== null && getKey(listCustomerAssignment.status) !== undefined
      ? getKey(listCustomerAssignment.status)
      : undefined
  );
  const [page, setPage] = useState<number>(
    Number(getKey(listCustomerAssignment.page)) || 1
  );
  const [pageLimit, setPageLimit] = useState<number>(
    Number(getKey(listCustomerAssignment.limit)) || PAGE_LIMIT
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [customers, setCustomers] = useState<any[]>([]);

  const statusOptions = [
    {
      value: "active",
      label: (
        <>
          <img src={green} width={12} height={12} className="me-3" alt="" />
          <span className="fs-16 fw-600 text-black">Active</span>
        </>
      ),
      title: "Active",
    },
    {
      value: "inactive",
      label: (
        <>
          <img src={red} width={12} height={12} className="me-3" alt="" />
          <span className="fs-16 fw-600 text-black">Inactive</span>
        </>
      ),
      title: "Inactive",
    },
  ];

  useEffect(() => {
    setFetchLoader(true);
    (async () => {
      await fetchCustomerAssignmentList(
        page,
        pageLimit,
        search,
        selectedStatus
      );
    })();
    setFetchLoader(false);
  }, []);

  const fetchCustomerAssignmentList = async (
    pageNo: number,
    limit: number,
    searchTerm: string,
    status: any
  ) => {
    setLoading(true);

    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      searchTerm: searchTerm,
      needCount: true,
      "assignedTo[0]": currentUser?._id,
    };

    if (status === "active") {
      params.blockState = false;
    } else if (status === "inactive") {
      params.blockState = true;
    }

    const apiService = new APICallService(
      buyer.listOnBoardedBuyers,
      params,
      "",
      "",
      "",
      "",
      AllCustomers,
    );

    let response = await apiService.callAPI();

    if (response) {
      const mappedCustomers = response.records.map((c: any) => ({
        _id: c._id,
        name: c.name || (c.business && c.business.name) || "-",
        phone: `${c.phoneCountry} ${c.phone}` || "-",
        ordersPlaced:
          c.analytics && c.analytics?.map((val: any) => val.totalOrders).reduce((a: number, b: number) => a + b, 0) || 0,
        totalSales:
          c.analytics && c.analytics?.map((val: any) => val.totalOrderValue).reduce((a: number, b: number) => a + b, 0) || 0,
        status: c.blocked ? "Inactive" : "Active",
        image: (c.business && c.business.image) || b1,
      }));
      setCustomers(mappedCustomers);
      setTotalRecords(response.total || 0);
    }

    setLoading(false);
  };

  const handleSearch = async (value: any) => {
    setSearch(value);
    setPage(1);
    setKey(listCustomerAssignment.search, value, false);
    setKey(listCustomerAssignment.page, 1);
    await fetchCustomerAssignmentList(
      1,
      pageLimit,
      value,
      selectedStatus
    );
  };

  const handleStatusFilter = async (value: any) => {
    setSelectedStatus(value);
    setKey(listCustomerAssignment.status, value);
    setPage(1);
    setKey(listCustomerAssignment.page, 1);
    await fetchCustomerAssignmentList(
      1,
      pageLimit,
      search,
      value
    );
  };

  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listCustomerAssignment.page, val);
    await fetchCustomerAssignmentList(
      val,
      pageLimit,
      search,
      selectedStatus
    );
  };

  const handlePageLimit = async (event: any) => {
    const limit = parseInt(event.target.value);
    setPage(1);
    setPageLimit(limit);
    setKey(listCustomerAssignment.page, 1);
    setKey(listCustomerAssignment.limit, limit);
    await fetchCustomerAssignmentList(
      1,
      limit,
      search,
      selectedStatus
    );
  };

  return (
    <div className="p-5">
      <Row className="align-items-center mb-7 g-5">
        <Col xs>
          <div className="d-flex justify-content-between">
            <h1 className="fs-22 fw-bolder">Customer Management</h1>
            {/* <div className="d-flex gap-4">
              <Button
                variant="primary"
                className="fs-16 fw-bold btn-lg px-7"
                onClick={() => { }}
              >
                Download Excel
              </Button>
            </div> */}
          </div>

          <Card className="bg-light border my-7">
            <Card.Body className="px-7">
              <Row className="align-items-center gy-5">
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500 text-dark">
                    Search by customer name and number
                  </FormLabel>
                  <div className="d-flex align-items-center position-relative">
                    <KTSVG
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon-3 position-absolute ms-3"
                    />
                    <input
                      type="text"
                      className="form-control form-control-custom borde-r8px bg-white ps-11"
                      placeholder="Search by customer name and number"
                      value={search}
                      onChange={(event: any) => {
                        handleSearch(event.target.value);
                      }}
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">
                    Filter by Status
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      value={statusOptions.find(
                        (option) => option?.value == selectedStatus
                      )}
                      options={statusOptions}
                      onChange={(event: any) =>
                        handleStatusFilter(event ? event.value : undefined)
                      }
                      isSearchable={false}
                      isMulti={false}
                      isClearable={true}
                      placeholder="Select Status"
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {!loading ? (
            customers && customers.length ? (
              <>
                <Card className="border border-r10px">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                        <thead>
                          <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                            <th className="min-w-250px">Customer Name</th>
                            <th className="min-w-150px">No. of Order Placed</th>
                            <th className="min-w-150px">Total Sales</th>
                            <th className="min-w-125px">Status</th>
                            <th className="min-w-100px text-end"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {customers.map((customer, index) => (
                            <tr key={customer._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="symbol symbol-45px me-5">
                                    <ImageWithFallback
                                      className="img-fluid border-r8px object-fit-contain"
                                      src={customer.image}
                                      alt={customer.name}
                                      style={{ borderRadius: "8px" }}
                                      noDefaultSize
                                    />
                                  </div>
                                  <div className="d-flex justify-content-start flex-column">
                                    <span className="text-dark fw-bolder fs-16 mb-1">
                                      {customer.name}
                                    </span>
                                    <span className="text-gray fw-500 fs-15 d-block">
                                      {customer.phone}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="fs-15 fw-500 text-dark">
                                  {customer.ordersPlaced
                                    .toString()
                                    .padStart(2, "0")}
                                </span>
                              </td>
                              <td>
                                <span className="fs-15 fw-500 text-dark">
                                  Tsh{" "}
                                  {Method.getGeneralizedAmount(
                                    customer.totalSales
                                  )}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge py-2 px-4 fs-14 rounded-pill ${customer.status === "Active"
                                    ? "bg-light-success text-success"
                                    : "bg-light-danger text-danger"
                                    }`}
                                >
                                  {customer.status}
                                </span>
                              </td>
                              <td className="text-end">
                                <Button
                                  className="uw-btn uw-btn-primary fs-14 fw-600 ms-4"
                                  variant="primary"
                                  onClick={() => {
                                    let currentScrollY = window.scrollY;
                                    localStorage.setItem("CustomerId", customer._id);
                                    setKey(listCustomerAssignment.scrollPosition, currentScrollY);
                                    setKey(listCustomerAssignment.search, search, false);
                                    navigateTo("/customers/customer-profile", { state: customer._id });
                                  }}
                                >
                                  View details
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>

                <div className="mt-7">
                  <Pagination
                    totalRecords={totalRecords}
                    currentPage={page}
                    handleCurrentPage={handleCurrentPage}
                    handleNextPage={() => handleCurrentPage(page + 1)}
                    handlePreviousPage={() => handleCurrentPage(page - 1)}
                    handlePageLimit={handlePageLimit}
                    pageLimit={pageLimit}
                  />
                </div>
              </>
            ) : (
              <div className="border border-r10px mb-6">
                <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                  <span className="fs-18 fw-500">No Customers Found</span>
                </div>
              </div>
            )
          ) : (
            <div className="border border-r10px mb-6">
              <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                <Loader loading={fetchLoader || loading} />
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CustomerManagement;
